<template>
  <SectionWithAnchor :anchorId="isFromLLP ? AnchorLinks.Articles : undefined">
    <div :class="$style.container">
      <h2 :class="$style.heading">{{ $t('landing.article.title') }}</h2>
      <p :class="$style.description">{{ $t('landing.article.description') }}</p>
      <HgSpinner v-if="isLoading" :class="$style.spinner" />
      <HgSlider
        v-else
        :breakpoints="sliderBreakpoints"
        :activeSlideIndex="0"
        :visibleSlides="4"
        :gap="20"
        type="slide"
        :sliderComponents="teasers"
        slideKey="title"
      >
        <template #default="sliderComponent">
          <HgTeaser :class="$style.cardWrapper" :href="sliderComponent.href" shouldOpenInNewWindow>
            <template #image>
              <img
                :class="$style.image"
                loading="lazy"
                :src="sliderComponent.img.src"
                :srcset="sliderComponent.img.srcset"
                :alt="sliderComponent.img.alt"
              />
            </template>
            <template #title>{{ sliderComponent.title }}</template>
            <template #text>
              {{ sliderComponent.text }}
            </template>
            <span :class="$style.link">
              {{ sliderComponent.linkText }}
            </span>
          </HgTeaser>
        </template>
      </HgSlider>
    </div>
  </SectionWithAnchor>
</template>

<script setup lang="ts">
import { HgSlider, HgSpinner, HgSupportedLanguage, HgTeaser } from '@smg-real-estate/domus';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onServerPrefetch, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import adaptData from '@/components/common/ArticleSection/adaptData';
import SectionWithAnchor from '@/components/common/SectionWithAnchor/SectionWithAnchor.vue';
import { isPlatformIs24 } from '@/helpers/Platform/Platform';
import { HG_BASE_PATH, IS24_BASE_PATH } from '@/router/routes/constants';
import { useCommonStore } from '@/stores/common/common';
import { useLandingStore } from '@/stores/landing/landing';
import { fetchArticles } from '@/stores/propertyValuation/services';
import type { Article } from '@/stores/types/landing';
import { AnchorLinks, BREAKPOINTS, Pages } from '@/types';

// Data
const articleBasePath = isPlatformIs24() ? IS24_BASE_PATH : HG_BASE_PATH;
const isLoading = ref(false);

// Store
const commonStore = useCommonStore();
const landingStore = useLandingStore();
const { articles } = storeToRefs(landingStore);

// Router and i18n
const { locale } = useI18n();

const { currentPage } = storeToRefs(commonStore);

// Other computed properties
const sliderBreakpoints = computed(() => ({
  [BREAKPOINTS.domus_lg - 0.1]: {
    perView: 3,
    peek: { before: 0, after: 40 },
  },
  [BREAKPOINTS.domus_md - 0.1]: {
    perView: 1,
    peek: { before: 0, after: 40 },
  },
}));

const isFromLLP = computed(() => currentPage.value === Pages.LLP);

const teasers = computed(() => adaptData(articles.value));

// Methods
const setArticles = (articleData: Article[]) => {
  landingStore.setArticles(articleData);
};

const loadArticles = async () => {
  isLoading.value = true;
  const articleData = await fetchArticles(articleBasePath, locale.value as HgSupportedLanguage);
  setArticles(articleData);
  isLoading.value = false;
};

// Watchers
watch(locale, loadArticles);

// Lifecycle
onMounted(() => {
  // For the case to switch EN to other languages on IS24
  // For the case to client side transition of jumping between LP and LLP
  if (!articles.value.length) {
    loadArticles();
  }
});

onServerPrefetch(async () => {
  await loadArticles();
});
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$article-image-height: 190px;
$spinner-height: 400px;

.container {
  margin-bottom: $space-500;

  section[class*='HgSlider_splide'] {
    div[class*='HgSlider_splideArrow'] {
      @include grid-media-query(md) {
        display: none;
      }
    }

    .image {
      width: 100%;
      height: $article-image-height;
      object-fit: cover;
    }
  }
}

.spinner {
  width: 100%;
  height: $spinner-height;
  padding-top: $space-200;
}

.heading {
  margin-top: 0;
  margin-bottom: $space-50;

  @include heading-2;
}

.description {
  margin-top: 0;
  margin-bottom: $space-250;

  @include body-2($color-grey-120);
}

.cardWrapper {
  box-sizing: border-box;
  height: 100%;
  padding: 3px;

  div[class*='HgTeaser_content_'] {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: calc(100% - $article-image-height);

    div[class*='HgTeaser_text_'] {
      margin-top: $space-50;
      margin-bottom: 0;
    }
  }

  > a[class*='HgCardElevated_link'] {
    height: 100%;

    &:hover,
    &:focus {
      box-shadow: $elevation-minimal;
    }

    &:focus::after {
      box-shadow: unset;
    }
  }
}

.link {
  margin-top: $space-50;
  color: $color-azul-110;

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}

.notificationMessage {
  align-self: stretch;
  margin: $space-150 0;
}
</style>
